import {
  $applyNodeReplacement,
  $getState,
  $getStateChange,
  createCommand,
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical';

import type { NoteEditorBlockDef } from '../../../config';

import { TemplateFieldHost } from './templateFieldWidgets';
import { templateInstructionRoleState } from './templateNodeState';

const markRole = (dom: HTMLElement, node: TemplateInstructionNode) => {
  dom.dataset.templateSlot = $getState(
    node,
    templateInstructionRoleState,
    'direct',
  );
  return dom;
};

type SerializedTemplateInstructionNode = Spread<
  { def: NoteEditorBlockDef },
  SerializedLexicalNode
>;

const EMPTY_DEF: NoteEditorBlockDef = {
  ID: '',
  SlotID: '',
  Kind: { case: 'FreeText', value: {} },
};

// An AI instruction in square brackets, one unit of the text, its text
// edited by its widget: a slot the AI writes text into, stored as a
// free-text block, or its section's guidance (`templateInstructionRoleState`),
// stored as the section's instructions.
export class TemplateInstructionNode extends DecoratorNode<JSX.Element> {
  __def: NoteEditorBlockDef;

  static getType(): string {
    return 'template-instruction';
  }

  static clone(node: TemplateInstructionNode): TemplateInstructionNode {
    return new TemplateInstructionNode(node.__def, node.__key);
  }

  static importJSON(
    json: SerializedTemplateInstructionNode,
  ): TemplateInstructionNode {
    return new TemplateInstructionNode(json.def).updateFromJSON(json);
  }

  constructor(def: NoteEditorBlockDef = EMPTY_DEF, key?: NodeKey) {
    super(key);
    this.__def = def;
  }

  exportJSON(): SerializedTemplateInstructionNode {
    return { ...super.exportJSON(), def: this.getDef() };
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('span');
    dom.dataset.noteBlockId = this.__def.ID;
    return markRole(dom, this);
  }

  updateDOM(previous: TemplateInstructionNode, dom: HTMLElement): boolean {
    if ($getStateChange(this, previous, templateInstructionRoleState)) {
      markRole(dom, this);
    }
    return false;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  decorate(): JSX.Element {
    return (
      <TemplateFieldHost
        kind="instruction"
        nodeKey={this.__key}
        def={this.__def}
        role={$getState(this, templateInstructionRoleState, 'direct')}
      />
    );
  }

  getDef(): NoteEditorBlockDef {
    return this.getLatest().__def;
  }

  getBlockId(): string {
    return this.getLatest().__def.ID;
  }

  setDef(def: NoteEditorBlockDef): this {
    const writable = this.getWritable();
    writable.__def = def;
    return writable;
  }
}

export const $createTemplateInstructionNode = (def: NoteEditorBlockDef) =>
  $applyNodeReplacement(new TemplateInstructionNode(def));

export const $isTemplateInstructionNode = (
  node: LexicalNode | null | undefined,
): node is TemplateInstructionNode => node instanceof TemplateInstructionNode;

// An empty instruction typed as `[]`: its text is typed next, in its own
// box, which the layer that renders it focuses.
export const TEMPLATE_INSTRUCTION_TYPED_COMMAND = createCommand<string>(
  'TEMPLATE_INSTRUCTION_TYPED_COMMAND',
);
